import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BroadcastService } from '../../common/services/broadcast/broadcast.service';
import { UserHttpService } from '../../user/services/user-http.service';
import { GenericFavoriteService, FavoriteSpace, FavoriteSpaces, FavoriteSpacesDict } from '../models/favorite';
import { FavoriteEvents } from '../enums/favorite-events.enum';
import { ToastService } from '../../common/services/toast/toast.service';
import { v4 } from 'uuid';
import { cloneDeep } from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class FavoriteSpacesService implements GenericFavoriteService<FavoriteSpacesDict, FavoriteSpace> {
    private key = 'favoriteSpaces';

    public favorites$: Observable<FavoriteSpacesDict>;
    private favoritesSubject: BehaviorSubject<FavoriteSpacesDict>;
    private favorites: FavoriteSpacesDict;
    private serverFavorites: any[];

    constructor(
        private broadcastService: BroadcastService,
        private httpService: UserHttpService,
        private toastService: ToastService
    ) {
        this.initDefaultValues();
        this.getFavoritesData();
    }

    private initDefaultValues() {
        this.favorites = {};
        this.favoritesSubject = new BehaviorSubject<FavoriteSpacesDict>(null);
        this.favorites$ = this.favoritesSubject.asObservable();
        this.serverFavorites = [];
    }

    private getFavoritesData() {
        this.getUserFavoritesSpaces().toPromise().then(result => {
            if (result != null) {
                for (const dictkey in result) {
                    if (result.hasOwnProperty(dictkey)) {
                        let favorite = result[dictkey];

                        //convert null to -1    
                        favorite.spaceId = favorite.spaceId == null ? -1 : favorite.spaceId;

                        //store this cos it has server key
                        this.serverFavorites.push(favorite);

                        //we just need to build that favorites dictionary with arrays of space ids from our flat dictionary returned from service
                        this.addFavoriteImp(favorite);
                    }
                }

                this.notifyObserversFavoritesChanged();
            }
        }).catch(reason => {
            // ignore
        });
    }

    private notifyObserversFavoritesChanged() {
        const next = JSON.parse(JSON.stringify(this.favorites));
        this.favoritesSubject.next(next);
    }

    public toggleFavorite(favorite: FavoriteSpace) {
        if (this.isFavorite(favorite)) {
            this.removeFavorite(favorite);
        } else {
            this.addFavorite(favorite);
        }
    }

    public isFavorite(favorite: FavoriteSpace) {
        if (this.favorites[favorite.locationId]) {
            return this.favorites[favorite.locationId].spaceIds.includes(favorite.spaceId);
        }
        return false;
    }

    private getServerKeys(favorite: FavoriteSpace):any[] {
        return this.serverFavorites.filter(item => item.locationId === favorite.locationId && item.spaceId === favorite.spaceId).map(item => item.id);
    }

    private removeFavorite(favorite: FavoriteSpace) {
        const serverKeys = this.getServerKeys(favorite);
        for (let i = 0; i < serverKeys.length; i++) {
            this.deleteUserFavoritesSpace(serverKeys[i]).toPromise().then(result => {
                if (result == true) {
                    let spaceIds = this.favorites[favorite.locationId].spaceIds;
                    spaceIds = spaceIds.filter(spaceId => spaceId !== favorite.spaceId);
                    this.favorites[favorite.locationId].spaceIds = spaceIds;

                    if (spaceIds.length === 0) {
                        delete this.favorites[favorite.locationId];
                    }

                    this.notifyObserversFavoritesChanged();
                    this.broadcastService.broadcast(FavoriteEvents.UnfavoriteSpace, favorite);
                    this.toastService.showSimple('Removed from favorites.');
                }
                const ind: number = this.serverFavorites.findIndex(item => item.id == serverKeys[i]);
                if (ind != -1) {
                    this.serverFavorites.splice(ind, 1);
                }
            });
        }
    }

    private addFavorite(favorite: FavoriteSpace) {
        this.postUserFavoritesSpace(cloneDeep(favorite)).toPromise().then(result => {
            if (result != null) {
                //convert null to -1    
                result.spaceId = result.spaceId == null ? -1 : result.spaceId;

                this.serverFavorites.push(result);

                this.addFavoriteImp(result);
                this.notifyObserversFavoritesChanged();
                this.broadcastService.broadcast(FavoriteEvents.FavoriteSpace, result);
                this.toastService.showSimple('Added to favorites.');
            }
        });
    }

    private addFavoriteImp(favorite: FavoriteSpace) {
        if (this.favorites[favorite.locationId]) {
            if (this.favorites[favorite.locationId].spaceIds.findIndex(item => item === favorite.spaceId) == -1) {
                this.favorites[favorite.locationId].spaceIds.push(favorite.spaceId);
            }
        } else {
            this.favorites[favorite.locationId] = {
                locationId: favorite.locationId,
                spaceIds: [favorite.spaceId]
            } as FavoriteSpaces;
        }
    }

    private postUserFavoritesSpace(favorite: any): Observable<any> {
        const id: string = v4();
        favorite['id'] = id;
        //convert -1 to null
        favorite.spaceId = favorite.spaceId === -1 ? null : favorite.spaceId;
        delete favorite['locationId'];
        return this.httpService.post<any>('user/favorites/spaces', favorite);
    }

    private deleteUserFavoritesSpace(id: string): Observable<boolean> {
        return this.httpService.delete<boolean>(`user/favorites/spaces/${id}`);
    }

    private getUserFavoritesSpaces(): Observable<any> {
        return this.httpService.get<any>('user/favorites/spaces');
    }
}
